import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useTalentPoolsApi from './config';
import { buildDepartmentAsync, buildLocationAsync, buildGradeAsync } from './asyncHelpers';
import { buildPoolPayload } from './poolPayload';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import OptionsMenu from '../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const Container = styled.div``;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.$dynamic ? `${p.theme.colors.secondary.main}1A` : `${p.theme.colors.primary.main}1A`)};
  color: ${(p) => (p.$dynamic ? p.theme.colors.secondary.dark : p.theme.colors.primary.dark)};
`;

const departmentAsync = buildDepartmentAsync();
const locationAsync = buildLocationAsync();
const gradeAsync = buildGradeAsync();

export default function TalentPoolPage() {
  const { t } = useTranslation();
  // Kademe kuralı yalnız İK (sunucuda 403; kademe kurallı havuz İK dışına görünmez — son inceleme M2).
  // İK = AuthContext.isHr (İK Rolü; superAdmin ve eski oturumda kurumsal yönetici dahil).
  const { canWrite, isHr = false } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({ open: false, id: null, name: '' });
  const [removing, setRemoving] = useState(false);

  const {
    fetching, data, loadFailed, columns: baseColumns,
    addPool, updatePool, removePool,
    poolTypeOptions, cellKeyOptions,
  } = useTalentPoolsApi();

  const schema = useMemo(() => [
    { name: 'name', label: t('performance.talentpool.poolName'), type: 'text', required: true, grid: 12 },
    {
      name: 'poolType',
      label: t('performance.talentpool.poolTypeLabel'),
      type: 'select',
      options: poolTypeOptions,
      required: true,
      grid: 12,
    },
    {
      name: 'ruleCellKeys',
      label: t('performance.talentpool.ruleCellKeys'),
      type: 'asyncSelect',
      isMulti: true,
      onSearch: async (q) => cellKeyOptions.filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase())),
      onGetById: async (id) => cellKeyOptions.find((o) => o.id === id) || { id, name: id },
      hidden: (f) => f.poolType !== 'dynamic',
      grid: 12,
    },
    {
      name: 'ruleDepartmentIds',
      label: t('performance.talentpool.ruleDepartmentIds'),
      type: 'asyncSelect',
      isMulti: true,
      onSearch: departmentAsync.onSearch,
      onGetById: departmentAsync.onGetById,
      hidden: (f) => f.poolType !== 'dynamic',
      grid: 12,
    },
    {
      name: 'ruleGradeIds',
      label: t('performance.talentpool.ruleGradeIds'),
      type: 'asyncSelect',
      isMulti: true,
      onSearch: gradeAsync.onSearch,
      onGetById: gradeAsync.onGetById,
      hidden: (f) => f.poolType !== 'dynamic' || !isHr,
      grid: 12,
    },
    {
      name: 'ruleMinSeniorityYears',
      label: t('performance.talentpool.ruleMinSeniorityYears'),
      type: 'number',
      hidden: (f) => f.poolType !== 'dynamic',
      grid: 6,
    },
    {
      name: 'ruleLocationIds',
      label: t('performance.talentpool.ruleLocationIds'),
      type: 'asyncSelect',
      isMulti: true,
      onSearch: locationAsync.onSearch,
      onGetById: locationAsync.onGetById,
      hidden: (f) => f.poolType !== 'dynamic',
      grid: 12,
    },
  ], [t, poolTypeOptions, cellKeyOptions, isHr]);

  const openAdd = useCallback(() => {
    setEditingPool(null);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setEditingPool({
      name: row.name,
      poolType: row.poolType,
      ruleCellKeys: row.ruleCellKeys || [],
      ruleDepartmentIds: row.ruleFilterJson?.departmentIds || [],
      ruleMinSeniorityYears: row.ruleFilterJson?.minSeniorityYears ?? '',
      ruleLocationIds: row.ruleFilterJson?.locationIds || [],
      ruleGradeIds: row.ruleFilterJson?.gradeIds || [],
      _id: row.id,
    });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const payload = buildPoolPayload(formData);
    if (editingPool?._id) {
      return updatePool(editingPool._id, payload);
    }
    return addPool(payload);
  }, [editingPool, addPool, updatePool]);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoving(true);
    const success = await removePool(removeConfirm.id);
    if (success) setRemoveConfirm({ open: false, id: null, name: '' });
    setRemoving(false);
  }, [removeConfirm.id, removePool]);

  const actionOptions = useMemo(() => {
    if (!canWrite('TD')) return [];
    return [
      { type: 'edit', label: t('performance.common.edit'), onClick: openEdit },
      { type: 'divider' },
      { type: 'remove', label: t('common.delete'), onClick: (row) => setRemoveConfirm({ open: true, id: row.id, name: row.name }) },
    ];
  }, [t, canWrite, openEdit]);

  const columns = useMemo(() => {
    const cols = [
      ...baseColumns,
      {
        name: 'poolTypeBadge',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return (
              <TypeBadge $dynamic={row.poolType === 'dynamic'}>
                {t(`performance.talentpool.poolType.${row.poolType}`, { defaultValue: row.poolType })}
              </TypeBadge>
            );
          },
        },
      },
    ];
    if (actionOptions.length > 0) {
      cols.push({
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      });
    }
    return cols;
  }, [baseColumns, actionOptions, data, t]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingPool(null); }}
        title={editingPool ? t('performance.common.edit') : t('performance.talentpool.addPool')}
        schema={schema}
        initialValues={editingPool || { poolType: 'static' }}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={removeConfirm.open}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: removeConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveConfirm({ open: false, id: null, name: '' })}
        loading={removing}
      />

      {/* TD-23 — başarısız okuma boş tabloya DEĞİL, uyarıya düşer. */}
      {loadFailed && <LoadErrorBanner />}

      <DataTable
        title={t('performance.talentpool.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        pageSize={20}
        canCreateNewRecord={canWrite('TD')}
        onNewButtonClick={openAdd}
        onRowClick={(row) => history.push(`/talent/pool/${row.id}`)}
      />
    </Container>
  );
}
